/**
 * Hook useKanbanColumn - Carrega deals de uma coluna do Kanban
 * Implementa cursor-based pagination com infinite scroll e filtros avançados
 * Versão: 1.3.0
 * RELEASE: 2026-09-07 v1.3.0 - Ponto de continuação passa a ser o PAR (data, id)
 *          e usa a data correspondente à ORDENAÇÃO escolhida (atualização,
 *          criação ou mudança de fase). Antes usava só updated_at e comparação
 *          estrita por data, o que sumia com registros gravados no mesmo
 *          instante (importações em lote) e travava a ordem crescente.
 * RELEASE: v1.2.0 - Adicionado envio de tag_ids para filtro por tags
 */

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { DealFilters } from '@/hooks/useCrmDeals';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('KanbanColumn');

// Tipo do deal retornado pelo RPC (v1.3.0 - adicionado seller)
export interface KanbanDeal {
  id: string;
  title: string;
  description?: string | null;
  // v2.4.0: Descrição HTML rica para o DealFormDialog
  description_html?: string | null;
  company_name: string | null;
  value: number;
  currency: string;
  priority: string;
  expected_close_date: string | null;
  contact_id: string | null;
  contact_name: string | null;
  contact_avatar: string | null;
  contact_phone: string | null;
  assigned_to: string | null;
  assigned_user_name: string | null;
  assigned_user_avatar: string | null;
  // v1.3.0: Campos do vendedor
  seller_id: string | null;
  seller_name: string | null;
  seller_avatar: string | null;
  // v1.4.0: Campos adicionais para o card
  source: string | null;
  probability: number | null;
  updated_at: string;
  created_at: string;
  // v1.5.0: tenant_id para corrigir bug de tags no modal do card
  tenant_id: string;
  // v1.6.0: status para exibir corretamente no card
  status: string;
  // v1.9.0: Último contrato do deal (via LEFT JOIN LATERAL em contracts)
  last_contract_status: string | null;
  last_contract_title: string | null;
  // v2.0.0: customer_id para abrir modal de cliente ao clicar no nome
  customer_id: string | null;
  // v2.2.0: Tags embutidas como JSONB array [{tag_id, tag_name, tag_color}]
  tags: Array<{ tag_id: string; tag_name: string; tag_color: string }>;
  // v2.3.0: Data de atualização da fase para ordenação dinâmica
  phase_updated_at: string | null;
  // v2.2.0 (RPC get_kanban_column): agregação inline do badge de cadência per_phase
  // Evita N+1 (1 RPC list_deal_cadence_steps por card) — vem zerado quando não há
  // cadência ativa de playbook per_phase na fase atual.
  cadence_phase_total: number;
  cadence_phase_pending: number;
  cadence_phase_has_overdue: boolean;
}

// Configurações de paginação
const PAGE_SIZE = 5; // v1.2.0: carga inicial reduzida (50 -> 5) para acelerar o primeiro render

/**
 * v1.3.0: ponto de continuação da rolagem — PAR (data, id).
 * O id é obrigatório para não perder cartões gravados no mesmo instante.
 */
type CursorColuna = { at: string; id: string } | null | undefined;

/**
 * Devolve a data usada na ordenação atual, que é a mesma que a RPC usa como
 * critério de continuação (updated_at, created_at ou phase_updated_at).
 */
function dataDeOrdenacao(deal: KanbanDeal, sortField: string): string | null {
  if (sortField === 'created_at') return deal.created_at ?? null;
  if (sortField === 'phase_updated_at') return deal.phase_updated_at ?? deal.updated_at ?? null;
  return deal.updated_at ?? null;
}



/**
 * Hook para carregar deals de uma coluna específica do Kanban
 * Utiliza cursor-based pagination para performance em escala
 * Suporta filtros avançados passados para a RPC
 * 
 * @param pipelineId - ID do pipeline
 * @param phaseId - ID da fase/coluna
 * @param filters - Filtros opcionais para a busca
 * @param sortField - Campo de ordenação ('created_at' | 'phase_updated_at' | 'updated_at')
 * @param sortDirection - Direção da ordenação ('asc' | 'desc')
 * @param enabled - Se deve buscar os dados (default: true)
 */
export function useKanbanColumn(
  pipelineId: string | null,
  phaseId: string | null,
  filters?: DealFilters,
  sortField: string = 'created_at',
  sortDirection: string = 'desc',
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    // Query key inclui filtros e ordenação para cache correto
    queryKey: ['kanban-column', pipelineId, phaseId, filters, sortField, sortDirection],
    queryFn: async ({ pageParam }) => {
      // v1.3.0: o cursor é um par (data, id) — sem o id, cartões gravados no
      // mesmo instante eram descartados ao rolar a coluna.
      const cursor = pageParam as CursorColuna;
      logger.debug(`Buscando deals da coluna ${phaseId}, cursor:`, cursor);

      // Chama RPC com parâmetros de filtro (v1.1.0)
      const { data, error } = await supabase.rpc('get_kanban_column', {
        p_pipeline_id: pipelineId!,
        p_phase_id: phaseId!,
        p_limit: PAGE_SIZE,
        p_cursor_updated_at: cursor?.at || null,
        p_cursor_id: cursor?.id || null,
        p_tenant_hint: currentTenant?.id || null,
        // Parâmetros de filtro (novos)
        p_search: filters?.search || null,
        p_assigned_to: filters?.assigned_to || null,
        p_seller_id: filters?.seller_id || null,
        p_status: filters?.status || null,
        p_priority: filters?.priority || null,
        p_probability_min: filters?.probability_min ?? null,
        p_probability_max: filters?.probability_max ?? null,
        p_source: filters?.source || null,
        p_product_id: filters?.product_id || null,
        p_category_id: filters?.category_id || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        // v2.3.0: Parâmetros de ordenação dinâmica
        p_sort_field: sortField || 'created_at',
        p_sort_direction: sortDirection || 'desc',
        // v3.0.0: Novos filtros de data
        p_created_from: filters?.created_from || null,
        p_created_to: filters?.created_to || null,
        p_won_date_from: filters?.won_date_from || null,
        p_won_date_to: filters?.won_date_to || null,
        p_lost_date_from: filters?.lost_date_from || null,
        p_lost_date_to: filters?.lost_date_to || null,
        p_phase_updated_from: filters?.phase_updated_from || null,
        p_phase_updated_to: filters?.phase_updated_to || null,
        // v3.0.0 (2026-07-11): Multi-seleção de origens + motivos de perda
        p_source_ids:
          filters?.source_ids && filters.source_ids.length > 0
            ? filters.source_ids
            : null,
        p_loss_reason_ids:
          filters?.loss_reason_ids && filters.loss_reason_ids.length > 0
            ? filters.loss_reason_ids
            : null,
        // v1.2.0 (2026-08-28): Multi-seleção de tags do negócio
        p_tag_ids:
          filters?.tag_ids && filters.tag_ids.length > 0
            ? filters.tag_ids
            : null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar coluna do Kanban:', error);
        throw error;
      }

      logger.debug(`Recebidos ${data?.length || 0} deals da coluna ${phaseId}`);
      return (data || []) as KanbanDeal[];
    },
    getNextPageParam: (lastPage): CursorColuna => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (lastPage.length < PAGE_SIZE) return undefined;
      const ultimo = lastPage[lastPage.length - 1];
      if (!ultimo) return undefined;
      // v1.3.0: cursor = data da ordenação em uso + id (desempate)
      const at = dataDeOrdenacao(ultimo, sortField);
      if (!at) return undefined;
      return { at, id: ultimo.id };
    },
    initialPageParam: null as CursorColuna,

    enabled: !!pipelineId && !!phaseId && !!currentTenant?.id && enabled,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
    refetchInterval: 30_000, // Polling fallback 30s — garante atualização se Realtime falhar
  });

  // Função para invalidar cache desta coluna
  const invalidateColumn = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['kanban-column', pipelineId, phaseId] 
    });
  };

  // Flatten all pages into a single array
  const deals = query.data?.pages.flat() || [];

  return {
    deals,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateColumn,
    error: query.error,
  };
}

/**
 * Hook para invalidar todas as colunas de um pipeline
 * Útil após operações que afetam múltiplas colunas (ex: mover deal)
 */
export function useInvalidateKanbanColumns() {
  const queryClient = useQueryClient();

  return (pipelineId: string) => {
    queryClient.invalidateQueries({
      queryKey: ['kanban-column', pipelineId],
    });
    // Também invalida os stats
    queryClient.invalidateQueries({
      queryKey: ['pipeline-phase-stats', pipelineId],
    });
  };
}
